/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, IconButton, Modal, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { Close as CloseIcon } from "@mui/icons-material";
import tol from "@assets/img/tooltip.svg";
import { InactivityHandler } from "@components/InactivityHandler";
import ButtonExpedienteStatus from "@components/ButtonExpedienteStatus";
import { BOFU_TABS_CONFIG } from "@helpers/constantes";
import CustomRightImage from "@components/CustomRightImagev2";
import TabBofu from "@components/TabBofu/TabBofu";
import DocumentsTabBofu from "@components/TabBofu/DocumentsTabBofu";
import {
  setAvalAdicionalIdInformation,
  setAvalAdicionalIdBankInformation,
  setBofuTabAvalDocument,
  setBofuTabAvalAdicionalDocument,
  setBofuTabAvalAdicionalConyugeDocument,
  setParamsIdentityValidationForm,
  setParamsConyugeForm,
} from "@store/slices/bofuSlice";
import ButtonContinue from "@components/TabBofu/ButtonContinue";
import { MATRIMONIAL_REGIMEN_CODES } from "@helpers/constantes";
import { CustomCompanyName } from "@components/CustomCompanyName";
import { PersonaStructure } from "@interfaces/index";
import { getPersonFullName } from "@helpers/funciones";

const SectionAvalesAdicionales = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const avalesAdicionales = useSelector((state: RootState) => state.bofu.avales_adicionales);

  const handleClickInformation = (avalAdicional: PersonaStructure) => {
    dispatch(setAvalAdicionalIdInformation(avalAdicional.id));
    setTimeout(() => {
      navigate("/producto/creditosimple/informacion_aval_adicional");
    }, 200);
  };

  const handleClickSend = (avalAdicional: PersonaStructure) => {
    dispatch(setAvalAdicionalIdInformation(avalAdicional.id));
    setTimeout(() => {
      navigate("/producto/creditosimple/aval_adicional_enviar");
    }, 200);
  };

  const handleClickIdentityValidation = (avalAdicional: PersonaStructure) => {
    dispatch(
      setParamsIdentityValidationForm({
        person_id: avalAdicional.id,
        person_type: "AVAL_ADICIONAL",
      }),
    );
    setTimeout(() => {
      navigate("/producto/creditosimple/validacion_identidad_aval_adicional");
    }, 200);
  };

  const handleClickBankInformation = (avalAdicional: PersonaStructure) => {
    dispatch(setAvalAdicionalIdBankInformation(avalAdicional.id));
    setTimeout(() => {
      navigate("/producto/creditosimple/info_bancaria_aval_adicional");
    }, 200);
  };

  const handleClickConyugeInformation = (personConyuge?: PersonaStructure) => {
    if (!personConyuge) return;
    dispatch(
      setParamsConyugeForm({
        person_id: personConyuge.id,
        person_type: "CONYUGE_AVAL_ADICIONAL",
      }),
    );
    setTimeout(() => {
      navigate("/producto/creditosimple/informacion_conyuge_aval_adicional");
    }, 200);
  };

  const handleClickAvalConyugeIdentityValidation = (personConyuge?: PersonaStructure) => {
    if (!personConyuge) return;
    dispatch(
      setParamsIdentityValidationForm({
        person_id: personConyuge.id,
        person_type: "CONYUGE_AVAL_ADICIONAL",
      }),
    );
    setTimeout(() => {
      navigate("/producto/creditosimple/validacion_identidad_conyugue_aval_adicional");
    }, 200);
  };

  if (avalesAdicionales.length === 0) {
    return null;
  }

  return (
    <Box mt={3}>
      <Typography variant={isMobileOrTablet ? "body2" : "body1"} fontWeight={700} color={theme?.palette?.primary?.dark}>
        Ahora carga los documentos relacionados al aval adicional
      </Typography>

      {avalesAdicionales.map((avalAdicional, index) => (
        <Box key={avalAdicional.persona.id} mt={2}>
          <Typography mt={2} variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.main}>
            {getPersonFullName(avalAdicional.persona)}
          </Typography>

          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              sx={{ flex: 1, textAlign: "left" }}
              color={theme?.palette?.primary?.dark}
            >
              Información aval adicional
            </Typography>

            <ButtonExpedienteStatus
              mode="form"
              formStatus={null}
              onClick={() => handleClickSend(avalAdicional.persona)}
            />
          </Box>

          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              sx={{ flex: 1, textAlign: "left" }}
              color={theme?.palette?.primary?.dark}
            >
              Información del aval
            </Typography>

            <ButtonExpedienteStatus
              mode="form"
              formStatus={avalAdicional.persona.personal_information_status}
              onClick={() => handleClickInformation(avalAdicional.persona)}
            />
          </Box>

          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              sx={{ flex: 1, textAlign: "left" }}
              color={theme?.palette?.primary?.dark}
            >
              Validación de identidad
            </Typography>

            <ButtonExpedienteStatus
              mode="validation"
              isCompleted={avalAdicional.persona.identity_validation_completed}
              documentStatus={avalAdicional.doc_validacion_identidad?.validated}
              onClick={() => handleClickIdentityValidation(avalAdicional.persona)}
            />
          </Box>

          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              sx={{ flex: 1, textAlign: "left" }}
              color={theme?.palette?.primary?.dark}
            >
              Información bancaria
            </Typography>

            <ButtonExpedienteStatus
              mode="form"
              formStatus={avalAdicional.persona.bank_references_status || null}
              onClick={() => handleClickBankInformation(avalAdicional.persona)}
            />
          </Box>

          <Box mt={2}>
            <DocumentsTabBofu
              inputPrefix={`aval_adicional_${index}`}
              tabCode={BOFU_TABS_CONFIG.tab_aval.code}
              documents={avalAdicional.documents}
              onChange={({ document, newValue }) => {
                dispatch(
                  setBofuTabAvalAdicionalDocument({
                    document,
                    newValue,
                    persona: avalAdicional.persona,
                    valueAction: undefined,
                    valueIndex: undefined,
                  }),
                );
              }}
              onChangeMultiple={({ document, newValue, valueAction, valueIndex }) => {
                dispatch(
                  setBofuTabAvalAdicionalDocument({
                    document,
                    newValue,
                    persona: avalAdicional.persona,
                    valueAction: valueAction || undefined,
                    valueIndex,
                  }),
                );
              }}
            />
          </Box>

          {avalAdicional.conyuge?.persona && (
            <Box mt={3}>
              <Box>
                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  fontWeight={700}
                  color={theme?.palette?.primary?.dark}
                >
                  Información del Conyuge del Aval adicional
                </Typography>

                <Typography mt={2} variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.main}>
                  {getPersonFullName(avalAdicional.conyuge.persona)}
                </Typography>

                <Box
                  mt={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  color={theme?.palette?.primary?.dark}
                >
                  <Typography variant={isMobileOrTablet ? "body2" : "body1"} sx={{ flex: 1, textAlign: "left" }}>
                    Datos generales
                  </Typography>

                  <ButtonExpedienteStatus
                    mode="form"
                    formStatus={avalAdicional.conyuge.persona.personal_information_status}
                    onClick={() => handleClickConyugeInformation(avalAdicional.conyuge?.persona)}
                  />
                </Box>

                {avalAdicional.conyuge.matrimonial_regime === MATRIMONIAL_REGIMEN_CODES.sociedad_conyugal.code && (
                  <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      sx={{ flex: 1, textAlign: "left" }}
                      color={theme?.palette?.primary?.dark}
                    >
                      Validación de identidad
                    </Typography>

                    <ButtonExpedienteStatus
                      mode="validation"
                      isCompleted={avalAdicional.conyuge.persona.identity_validation_completed}
                      documentStatus={avalAdicional.conyuge.doc_validacion_identidad?.validated}
                      onClick={() => handleClickAvalConyugeIdentityValidation(avalAdicional.conyuge?.persona)}
                    />
                  </Box>
                )}
              </Box>

              <Typography
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={700}
                color={theme?.palette?.primary?.dark}
                mt={5}
                mb={4}
              >
                Ahora carga los documentos relacionados al conyuge
              </Typography>

              <DocumentsTabBofu
                inputPrefix={`conyuge_aval_adicional_${index}`}
                tabCode={BOFU_TABS_CONFIG.tab_aval.code}
                documents={avalAdicional.conyuge.documents}
                onChange={({ document, newValue }) => {
                  dispatch(
                    setBofuTabAvalAdicionalConyugeDocument({
                      document,
                      newValue,
                      persona: avalAdicional.conyuge?.persona,
                      valueAction: undefined,
                      valueIndex: undefined,
                    }),
                  );
                }}
                onChangeMultiple={({ document, newValue, valueAction, valueIndex }) => {
                  dispatch(
                    setBofuTabAvalAdicionalConyugeDocument({
                      document,
                      newValue,
                      persona: avalAdicional.conyuge?.persona,
                      valueAction: valueAction || undefined,
                      valueIndex,
                    }),
                  );
                }}
              />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

const SectionConyugeAval = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const personConyugeAval = useSelector((state: RootState) => state.bofu.aval_conyuge);
  const showInformationFormCoyuge = useSelector(
    (state: RootState) => state.bofu.bofu_tabs_obj.aval.show_information_form_conyuge,
  );
  const regimenMatrimonialAval = useSelector((state: RootState) => state.bofu.aval_regimen_conyugal);
  const bofuTabs = useSelector((state: RootState) => state.bofu.bofu_tabs_obj);

  const handleClickInformation = (personConyuge: PersonaStructure) => {
    dispatch(
      setParamsConyugeForm({
        person_id: personConyuge.id,
        person_type: "CONYUGE_AVAL",
      }),
    );
    navigate("/producto/creditosimple/informacion_conyuge_aval");
  };

  const handleClickIdentityValidation = (personConyuge: PersonaStructure) => {
    dispatch(
      setParamsIdentityValidationForm({
        person_id: personConyuge.id,
        person_type: "CONYUGE_AVAL",
      }),
    );
    navigate("/producto/creditosimple/validacion_identidad_conyugue_aval");
  };

  if (!personConyugeAval) {
    return null;
  }

  return (
    <Box mt={3}>
      <Box>
        <Typography
          variant={isMobileOrTablet ? "body2" : "body1"}
          fontWeight={700}
          color={theme?.palette?.primary?.dark}
        >
          Información del Conyuge del Aval
        </Typography>

        <Typography mt={2} variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.main}>
          {getPersonFullName(personConyugeAval)}
        </Typography>

        {showInformationFormCoyuge && (
          <Box
            mt={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            color={theme?.palette?.primary?.dark}
          >
            <Typography variant={isMobileOrTablet ? "body2" : "body1"} sx={{ flex: 1, textAlign: "left" }}>
              Datos generales
            </Typography>

            <ButtonExpedienteStatus
              mode="form"
              formStatus={personConyugeAval?.personal_information_status}
              onClick={() => handleClickInformation(personConyugeAval)}
            />
          </Box>
        )}

        {regimenMatrimonialAval === MATRIMONIAL_REGIMEN_CODES.sociedad_conyugal.code && (
          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              sx={{ flex: 1, textAlign: "left" }}
              color={theme?.palette?.primary?.dark}
            >
              Validación de identidad
            </Typography>

            <ButtonExpedienteStatus
              mode="validation"
              isCompleted={personConyugeAval?.identity_validation_completed}
              documentStatus={bofuTabs.aval.doc_validacion_identidad_conyuge?.validated}
              onClick={() => handleClickIdentityValidation(personConyugeAval)}
            />
          </Box>
        )}
      </Box>

      <Typography
        variant={isMobileOrTablet ? "body2" : "body1"}
        fontWeight={700}
        color={theme?.palette?.primary?.dark}
        mt={5}
        mb={4}
      >
        Ahora carga los documentos relacionados al conyuge
      </Typography>

      <DocumentsTabBofu
        inputPrefix="conyuge_aval"
        tabCode={BOFU_TABS_CONFIG.tab_aval.code}
        documents={bofuTabs?.aval?.documents_conyuge || []}
        onChange={({ document, newValue }) => {
          dispatch(
            setBofuTabAvalDocument({
              document,
              newValue,
            }),
          );
        }}
      />
    </Box>
  );
};

const DocsAval = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();

  const bufuLoading = useSelector((state: RootState) => state.bofu.isLoading);
  const personAval = useSelector((state: RootState) => state.bofu.aval);
  const representantes = useSelector((state: RootState) => state.bofu.bofu_tabs_obj.representantes.items);
  const bofuTabs = useSelector((state: RootState) => state.bofu.bofu_tabs_obj);

  const [open, setOpen] = useState(false);
  const [showIdentityValidationAval, setShowIdentityValidationAval] = useState<boolean>(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleClickIndentityValidationAval = () => {
    dispatch(
      setParamsIdentityValidationForm({
        person_id: personAval?.id,
        person_type: "AVAL",
      }),
    );
    setTimeout(() => {
      navigate("/producto/creditosimple/validacion_identidad_aval");
    }, 200);
  };

  useEffect(() => {
    (async () => {
      if (!personAval) {
        navigate("/producto/creditosimple/docs_empresa");
        return;
      }

      const representantes_ids = representantes.map((representante) => representante.persona.id);
      const is_same_person = representantes_ids.includes(personAval?.id);
      setShowIdentityValidationAval(is_same_person ? false : true);
    })();
  }, []);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8} mt={2}>
          <Box textAlign={"center"} mx={4}>
            <InactivityHandler />

            <CustomCompanyName />

            <TabBofu />

            {bufuLoading ? (
              <Box mt={3}>
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
              </Box>
            ) : (
              <Box mt={4}>
                {bofuTabs?.aval?.show_form_aval && (
                  <Box>
                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={700}
                      color={theme?.palette?.primary?.dark}
                    >
                      Ahora es momento de llenar los formatos relacionados al aval{" "}
                      <img src={tol} alt="" onClick={handleOpen} />
                    </Typography>

                    <Typography
                      mt={2}
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      color={theme?.palette?.primary?.main}
                    >
                      {`${personAval?.name || ""} ${personAval?.last_name || ""} ${personAval?.last_name_2 || ""}`}
                    </Typography>

                    <Box
                      mt={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      color={theme?.palette?.primary?.dark}
                    >
                      <Typography variant={isMobileOrTablet ? "body2" : "body1"} sx={{ flex: 1, textAlign: "left" }}>
                        Información del aval
                      </Typography>

                      <ButtonExpedienteStatus
                        mode="form"
                        formStatus={personAval?.personal_information_status || null}
                        onClick={() => navigate("/producto/creditosimple/informacion_datos_aval")}
                      />
                    </Box>

                    {showIdentityValidationAval && (
                      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant={isMobileOrTablet ? "body2" : "body1"}
                          sx={{ flex: 1, textAlign: "left" }}
                          color={theme?.palette?.primary?.dark}
                        >
                          Validación de identidad
                        </Typography>

                        <ButtonExpedienteStatus
                          mode="validation"
                          isCompleted={personAval?.identity_validation_completed}
                          documentStatus={bofuTabs.aval.doc_validacion_identidad?.validated}
                          onClick={() => handleClickIndentityValidationAval()}
                        />
                      </Box>
                    )}

                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        sx={{ flex: 1, textAlign: "left" }}
                        color={theme?.palette?.primary?.dark}
                      >
                        Información bancaria
                      </Typography>

                      <ButtonExpedienteStatus
                        mode="form"
                        formStatus={personAval?.bank_references_status || null}
                        onClick={() => navigate("/producto/creditosimple/info_bancaria_aval")}
                      />
                    </Box>

                    {bofuTabs?.aval?.documents.length > 0 && (
                      <Box>
                        <Typography
                          variant={isMobileOrTablet ? "body2" : "body1"}
                          fontWeight={700}
                          color={theme?.palette?.primary?.dark}
                          mt={4}
                        >
                          Ahora carga los documentos relacionados al aval
                        </Typography>

                        <DocumentsTabBofu
                          inputPrefix="aval"
                          tabCode={BOFU_TABS_CONFIG.tab_aval.code}
                          documents={bofuTabs?.aval?.documents || []}
                          onChange={({ document, newValue }) => {
                            dispatch(
                              setBofuTabAvalDocument({
                                document,
                                newValue,
                              }),
                            );
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                <SectionConyugeAval />

                <SectionAvalesAdicionales />

                <Box mt={4}>
                  <ButtonContinue tabCode={BOFU_TABS_CONFIG.tab_aval.code} />
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>

      <Modal open={open} onClose={handleClose} aria-labelledby="modal-title" aria-describedby="modal-description">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "250px", sm: "300px", md: "60vw", lg: "40vw" },
            maxWidth: 500,
            bgcolor: "background.paper",
            border: "2px solid #002652",
            borderRadius: "10px",
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              color: "#76A1FF",
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            id="modal-title"
            variant="h6"
            component="h2"
            fontWeight={600}
            color={theme?.palette?.primary?.dark}
          >
            Aval
          </Typography>

          <Typography id="modal-description" sx={{ mt: 2, color: theme?.palette?.primary?.dark }}>
            Es el accionista mayoritario de la empresa, que a su vez, será la persona encargada de asegurar que se
            saldará la deuda.
          </Typography>
        </Box>
      </Modal>
    </>
  );
};

export default DocsAval;
